import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, API, ModifyUserOfAbility, CommonAbilityService, Ability, AbilityFilter, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios, Client } from '@umanforge/common';
import { ModifyAbility, PostAbility } from 'interfaces/api_ability.interfaces';
import { ImportError } from 'interfaces/api_import.interfaces';
import { isHeaderProblem } from 'utils/import.utils';


export class AbilityService {

  static getAbility(
    abilityId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Ability> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonAbilityService.getAbility( abilityId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowAbilities(
    filters: AbilityFilter | null,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Ability[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonAbilityService.getAllowAbilities( filters, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedAbilitiesBySitesId(
    sitesId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Ability[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonAbilityService.getAllowedAbilitiesBySitesId( sitesId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static postAbility(
    body: PostAbility,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    return new Promise( async resolve => {
      AXIOS.post( API.ABILITIES, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 ? response.data.id : null );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( null );
        } );
    } );
  }

  static deleteAbility(
    abilityId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.ABILITIES + abilityId, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static putAbility(
    abilityId: UUID, body: ModifyAbility,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.ABILITIES + abilityId, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static postUsersToAbility(
    abilityId: UUID, body: { users: ModifyUserOfAbility[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.ABILITIES + abilityId + '/users', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
        } );
    } );
  }

  static deleteUsersToAbility(
    abilityId: UUID, body: { users: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.ABILITIES + abilityId + '/users', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
        } );
    } );
  }

  static exportAbiltiesOfClient(
    client: Client,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios,
    filters?: { filterSites?: UUID[] }
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.ABILITIES }export/${ client.id }`, {
        ...await CommonGlobalService.getHeaderRequest( token ),
        params: filters,
        responseType: 'blob'
      } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = `${ client.name }_abilties_export.xlsx`;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static downloadAbilitiesTemplate(
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.ABILITIES }export`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = 'abilities_template.xlsx';
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static importAbiltiesOnClient(
    clientId: UUID, file: File, hasHeader: boolean | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID[] | ImportError | null> {
    return new Promise( async resolve => {

      const formData = new FormData();
      formData.append( 'content', file );
      if ( hasHeader !== undefined ) {
        formData.append( 'hasHeader', String( hasHeader ) );
      }
      const requestConfig = await CommonGlobalService.getHeaderRequest( token );
      ( requestConfig.headers ?? {} )[ 'Content-Type' ] = 'multipart/form-data';

      AXIOS.post( `${ API.ABILITIES }import/${ clientId }`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          let error: ImportError | null = null;
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 && !( isHeaderProblem( response.data ) && hasHeader === undefined ) ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 202 ) {
            error = response.data as ImportError;
          }

          resolve( response.status === 200 ? response.data.ids : error );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

};